/**
 * ResumePlanTourChip — a tiny, dismissible chip that appears on a business's
 * sub-pages after the founder clicks "Open this page" inside the Plan Actions
 * Tour. Clicking it returns to the plan, where the tour resumes where it left
 * off. Rendered once globally (inside the Router) so it can show on any
 * /business/:id/* page, even ones that don't host the actions panel.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X as XIcon } from "lucide-react";
import { readTourResume, clearTourResume } from "../lib/planTourResume";

export default function ResumePlanTourChip() {
  const location = useLocation();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);

  useEffect(() => {
    setResume(readTourResume());
  }, [location.pathname]);

  if (!resume) return null;

  // Only on the matching business's sub-pages, and not on the plan page itself
  // (there the tour resumes on its own).
  const base = `/business/${resume.businessId}`;
  const onThisBusiness = location.pathname.startsWith(`${base}/`) || location.pathname === base;
  const onPlanPage = location.pathname === `${base}/plan`;
  if (!onThisBusiness || onPlanPage) return null;

  return (
    <div
      data-testid="resume-plan-tour-chip"
      className="hidden md:flex fixed bottom-6 left-6 z-40 items-center gap-2 bg-neutral-900 text-white shadow-2xl border-2 border-[#7C5FB0] pl-3 pr-1 py-1.5"
    >
      <button
        type="button"
        onClick={() => navigate(`${base}/plan`)}
        data-testid="resume-plan-tour-btn"
        className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold hover:text-[#C9B8E8]"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#C9B8E8]" />
        Resume tour
      </button>
      <button
        type="button"
        onClick={() => {
          clearTourResume();
          setResume(null);
        }}
        data-testid="resume-plan-tour-dismiss"
        aria-label="Dismiss resume tour"
        className="p-1 hover:bg-[#5E4689]"
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
